/**
 * 路径 → 选区（M3-15 / DESIGN.md §5.1、§6.3）。
 *
 * 子扫描线覆盖度算法与 engine/selection/ops.ts 的 fromPolygon 同一口径
 * （SUB=4 子采样、按像素区间部分覆盖），但泛化到**多子路径 + 缠绕规则**：
 * 钢笔路径可以有多个闭合子路径（比如字母 O 的内外两环），evenodd 天生
 * 支持这种"挖洞"，nonzero 则按穿越方向累计缠绕数，非零处即为内部。
 *
 * TileRowWriter 与 ops.ts 内部那个类结构相同但不共享——ops.ts 没有导出它，
 * 这里的版本更简单（不需要 base 之外的参数），各自独立维护成本可接受。
 */
import { TILE } from '../tile/key';
import { SelectionMask } from '../selection/mask';
import { flattenPath } from './flatten';
import type { Point } from './anchor';

class TileRowWriter {
  readonly #tiles = new Map<string, Uint8Array>();
  constructor(private readonly x0: number) {}

  writeRow(y: number, row: Uint8Array): void {
    const ty = Math.floor(y / TILE);
    const rowOffset = (y % TILE) * TILE;
    let i = 0;
    while (i < row.length) {
      const docX = this.x0 + i;
      const tx = Math.floor(docX / TILE);
      const segment = Math.min((tx + 1) * TILE - docX, row.length - i);
      const id = `${tx} ${ty}`;
      let tile = this.#tiles.get(id);
      if (tile === undefined) {
        tile = new Uint8Array(TILE * TILE);
        this.#tiles.set(id, tile);
      }
      tile.set(row.subarray(i, i + segment), rowOffset + (docX % TILE));
      i += segment;
    }
  }

  build(width: number, height: number): SelectionMask {
    return SelectionMask.build(width, height, 0, this.#tiles);
  }
}

interface Edge {
  readonly a: Point;
  readonly b: Point;
}

function collectEdges(subpaths: ReadonlyArray<{ points: readonly Point[] }>): Edge[] {
  const edges: Edge[] = [];
  for (const sub of subpaths) {
    const pts = sub.points;
    if (pts.length < 2) continue;
    for (let i = 0; i < pts.length; i++) {
      const a = pts[i]!;
      const b = pts[(i + 1) % pts.length]!;
      if (a.y !== b.y) edges.push({ a, b });
    }
  }
  return edges;
}

/** 一条扫描线与全部边的交点（含方向，供 nonzero 缠绕数用）。 */
function scanlineCrossings(
  edges: readonly Edge[],
  sy: number,
): Array<{ x: number; dir: 1 | -1 }> {
  const hits: Array<{ x: number; dir: 1 | -1 }> = [];
  for (const { a, b } of edges) {
    if ((sy >= a.y && sy < b.y) || (sy >= b.y && sy < a.y)) {
      const x = a.x + ((sy - a.y) / (b.y - a.y)) * (b.x - a.x);
      hits.push({ x, dir: b.y > a.y ? 1 : -1 });
    }
  }
  hits.sort((p, q) => p.x - q.x);
  return hits;
}

/** 由排序后的交点求「内部」区间：nonzero 按累计缠绕数，evenodd 按奇偶配对。 */
function insideSpans(
  hits: ReadonlyArray<{ x: number; dir: 1 | -1 }>,
  fillRule: 'nonzero' | 'evenodd',
): Array<[number, number]> {
  const spans: Array<[number, number]> = [];
  if (fillRule === 'evenodd') {
    for (let i = 0; i + 1 < hits.length; i += 2) spans.push([hits[i]!.x, hits[i + 1]!.x]);
    return spans;
  }
  let winding = 0;
  let spanStart: number | null = null;
  for (const hit of hits) {
    const wasInside = winding !== 0;
    winding += hit.dir;
    const isInside = winding !== 0;
    if (!wasInside && isInside) spanStart = hit.x;
    else if (wasInside && !isInside && spanStart !== null) {
      spans.push([spanStart, hit.x]);
      spanStart = null;
    }
  }
  return spans;
}

const SUB = 4;

/** 把展平后的多子路径栅格化为选区蒙版（AA，子扫描线覆盖度）。 */
export function subpathsToSelection(
  width: number,
  height: number,
  subpaths: ReadonlyArray<{ points: readonly Point[] }>,
  fillRule: 'nonzero' | 'evenodd',
): SelectionMask {
  const edges = collectEdges(subpaths);
  if (edges.length === 0) return SelectionMask.empty(width, height);

  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const sub of subpaths) {
    for (const p of sub.points) {
      minX = Math.min(minX, p.x);
      minY = Math.min(minY, p.y);
      maxX = Math.max(maxX, p.x);
      maxY = Math.max(maxY, p.y);
    }
  }
  const y0 = Math.max(0, Math.floor(minY));
  const y1 = Math.min(height, Math.ceil(maxY));
  const x0 = Math.max(0, Math.floor(minX));
  const x1 = Math.min(width, Math.ceil(maxX));
  if (y0 >= y1 || x0 >= x1) return SelectionMask.empty(width, height);

  const rowWidth = x1 - x0;
  const coverage = new Float32Array(rowWidth);
  const row = new Uint8Array(rowWidth);
  const writer = new TileRowWriter(x0);

  for (let y = y0; y < y1; y++) {
    coverage.fill(0);
    for (let sub = 0; sub < SUB; sub++) {
      const sy = y + (sub + 0.5) / SUB;
      const hits = scanlineCrossings(edges, sy);
      for (const [from0, to0] of insideSpans(hits, fillRule)) {
        const from = Math.max(x0, from0);
        const to = Math.min(x1, to0);
        if (from >= to) continue;
        const first = Math.floor(from);
        const last = Math.ceil(to) - 1;
        for (let px = first; px <= last; px++) {
          const overlap = Math.min(px + 1, to) - Math.max(px, from);
          if (overlap > 0) coverage[px - x0] = coverage[px - x0]! + overlap / SUB;
        }
      }
    }
    for (let i = 0; i < rowWidth; i++) row[i] = Math.round(Math.min(1, coverage[i]!) * 255);
    writer.writeRow(y, row);
  }
  return writer.build(width, height);
}

/** 路径命令字符串 → 选区（钢笔的「路径转选区」，Ctrl+Enter 惯例）。 */
export function pathToSelection(
  width: number,
  height: number,
  commands: string,
  fillRule: 'nonzero' | 'evenodd',
): SelectionMask {
  return subpathsToSelection(width, height, flattenPath(commands), fillRule);
}
